import { useEffect, useRef, useState, useCallback } from 'react';
import config from '../config';
import { generateCorrelationId } from '../utils/api';
import { getAccessToken, getRefreshToken, getUserInfo } from '../utils/auth';
import { refreshAndPersistTokens } from '../utils/httpApi';
import { debugLog, debugWarn, debugError } from '../utils/debug';
import { diag, setDiagProbe } from '../utils/diag';

const RECONNECT_DELAY = 10000; // 10 seconds - base reconnect delay
const MAX_RECONNECT_DELAY = 30000; // cap on exponential backoff
const PING_INTERVAL = 10000; // 10 seconds - send ping to keep connection alive
const DATA_UPDATE_THROTTLE = 250; // Throttle data updates to max 4 times per second (250ms)
// If we've sent pings but seen no pong (or any inbound message) for this long,
// the socket is dead-but-looks-open — the classic state Firefox leaves behind
// after a laptop resumes from overnight sleep. We proactively tear it down and
// reconnect rather than trusting readyState, which still reports OPEN.
const LIVENESS_TIMEOUT = 30000; // 30 seconds

// Note: userEmail and authCode parameters are deprecated and no longer used
// Login/authorization now handled via HTTP API - WebSocket validates JWT
function useWebSocket(userEmail, authCode, onAuthResponse, isAuthenticated = false, onAuthFailed = null, selectedReceiverUuid = 'ALL_RECEIVERS') {
  const [status, setStatus] = useState('connecting');
  const [data, setData] = useState(null);
  const [furthestDistanceData, setFurthestDistanceData] = useState(null);
  const [allTimeFurthestDistanceData, setAllTimeFurthestDistanceData] = useState(null);
  const [receiverStatistics, setReceiverStatistics] = useState(null);
  const [connectionEvents, setConnectionEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [updatesPerSecond, setUpdatesPerSecond] = useState(0);
  const [airportActivityUpdate, setAirportActivityUpdate] = useState(null);
  const [acarsMessages, setAcarsMessages] = useState([]);
  const [notamUpdates, setNotamUpdates] = useState([]);
  const [predictionUpdate, setPredictionUpdate] = useState(null);
  const [departureForecastUpdate, setDepartureForecastUpdate] = useState(null);
  // Bumped on every socket open. The stream is incremental only, and the 60s sweep below
  // drops every aircraft (latched positions included) during an outage longer than that,
  // so consumers that seed the store from an HTTP snapshot must re-run per connection,
  // not just per receiver change.
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const seenTimerRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const updateCounterRef = useRef(0);
  // Connection-lifecycle guards (Firefox overnight reconnect-storm fix):
  //   connectingRef      - a connect() is mid-flight; blocks overlapping sockets
  //   reconnectAttemptsRef - backoff exponent, reset to 0 on a healthy open
  //   lastInboundAtRef   - ms timestamp of the last message (incl. pong) received
  //   connectRef         - always points at the latest connect() closure so the
  //                        reconnect timer / watchdog invoke the current one
  const connectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const lastInboundAtRef = useRef(0);
  const connectRef = useRef(null);
  // Cumulative lifetime counters — stamped into every diagnostics heartbeat so a
  // reconnect storm is visible as a fast-climbing count even without heap data
  // (Firefox doesn't expose heap size). Healthy = these stay near-flat.
  const totalConnectsRef = useRef(0);
  const totalClosesRef = useRef(0);
  const totalMessagesRef = useRef(0);

  // Feed live socket/session state into the diagnostics heartbeat.
  useEffect(() => {
    setDiagProbe(() => ({
      ws: wsRef.current ? wsRef.current.readyState : -1, // 0 CONNECTING 1 OPEN 2 CLOSING 3 CLOSED, -1 none
      connecting: connectingRef.current,
      attempts: reconnectAttemptsRef.current,
      connects: totalConnectsRef.current,
      closes: totalClosesRef.current,
      msgs: totalMessagesRef.current,
      sinceInboundSec: lastInboundAtRef.current
        ? Math.round((Date.now() - lastInboundAtRef.current) / 1000)
        : null,
    }));
  }, []);
  const lastReceiverStatsRef = useRef(null);
  const userEmailRef = useRef(userEmail);
  const authCodeRef = useRef(authCode);
  const onAuthResponseRef = useRef(onAuthResponse);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const onAuthFailedRef = useRef(onAuthFailed);
  const selectedReceiverUuidRef = useRef(selectedReceiverUuid);

  // Throttling refs for data updates - reduces re-renders to improve touch responsiveness
  const pendingDataRef = useRef(null); // Accumulates aircraft data between flushes
  const dataFlushTimerRef = useRef(null); // Timer for flushing accumulated data
  const lastDataFlushRef = useRef(0); // Timestamp of last data flush

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => {
      // Cap to the most recent 200 entries (matching connectionEvents / notamUpdates
      // / acarsMessages). Previously unbounded — addLog fires at least every 10s via
      // the ping (sendMessage -> "Sent ...") for the whole session, so the array grew
      // forever and held in state, leaking memory until the Live tab crashed.
      const updated = [...prev, `[${timestamp}] ${message}`];
      return updated.length > 200 ? updated.slice(-200) : updated;
    });
  };

  // Update refs when props change
  // IMPORTANT: Update selectedReceiverUuidRef synchronously (outside useEffect) to prevent race conditions
  // where WebSocket messages are processed with stale receiver selection
  selectedReceiverUuidRef.current = selectedReceiverUuid;

  useEffect(() => {
    userEmailRef.current = userEmail;
    authCodeRef.current = authCode;
    onAuthResponseRef.current = onAuthResponse;
    isAuthenticatedRef.current = isAuthenticated;
    onAuthFailedRef.current = onAuthFailed;
  }, [userEmail, authCode, onAuthResponse, isAuthenticated, onAuthFailed]);

  // Track previous receiver to detect changes (not just initial mount)
  const prevReceiverUuidRef = useRef(selectedReceiverUuid);

  // Filter/clear aircraft data when receiver selection changes
  useEffect(() => {
    const prevReceiver = prevReceiverUuidRef.current;
    const isSameReceiver = prevReceiver === selectedReceiverUuid;
    prevReceiverUuidRef.current = selectedReceiverUuid;

    debugLog(`🧹 Receiver selection: ${prevReceiver} -> ${selectedReceiverUuid} (same: ${isSameReceiver})`);

    // If receiver hasn't actually changed, preserve existing data
    // This handles mode switches (Airport -> Live) with the same receiver
    if (isSameReceiver) {
      debugLog('🧹 Same receiver - preserving existing aircraft data');
      return;
    }

    debugLog(`🧹 Receiver CHANGED from ${prevReceiver} to ${selectedReceiverUuid} - clearing data`);

    // IMPORTANT: Cancel any pending data flush timer to prevent race condition
    // where old receiver's data gets flushed after the receiver change
    if (dataFlushTimerRef.current) {
      clearTimeout(dataFlushTimerRef.current);
      dataFlushTimerRef.current = null;
      debugLog('🧹 Cancelled pending data flush timer');
    }

    // Clear ALL pending data when receiver changes - don't try to filter it
    // This prevents any late-arriving data from the old receiver being added
    if (pendingDataRef.current) {
      const pendingCount = Object.keys(pendingDataRef.current).length;
      debugLog(`🧹 Clearing ${pendingCount} pending aircraft updates`);
      pendingDataRef.current = null;
    }

    // Notify backend of receiver selection change (if connected)
    // This ensures backend sends data for the correct receiver
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const token = getAccessToken();
      if (token) {
        debugLog(`📡 Notifying backend of receiver change: ${selectedReceiverUuid}`);
        const message = {
          header: {
            token: token,
            service: 'WebsocketEndpoint',
            function: 'selectReceiver',
            correlationID: generateCorrelationId()
          },
          receiverUuid: selectedReceiverUuid
        };
        wsRef.current.send(JSON.stringify(message));
      }
    }

    // Clear aircraft data when receiver changes to prevent cross-contamination
    // This ensures a clean slate - new data will come from the backend for the new receiver
    setData(prevData => {
      if (!prevData || Object.keys(prevData).length === 0) {
        return {};
      }

      const prevCount = Object.keys(prevData).length;
      debugLog(`🧹 Clearing all ${prevCount} aircraft - waiting for fresh data from receiver ${selectedReceiverUuid}`);
      return {};
    });

    // Also clear furthest distance data for clean slate
    setFurthestDistanceData(null);
    setAllTimeFurthestDistanceData(null);
  }, [selectedReceiverUuid]);

  // Refresh access token. Delegates to the shared single-flight helper in
  // httpApi.js so the HTTP 401-retry path and the WebSocket close-reauth
  // path share one in-flight request. Without this, both layers could fire
  // refreshes in parallel — and now that the backend rotates the refresh
  // token on every refresh, the second request would use a stale token and
  // fail, kicking the user out.
  const performTokenRefresh = useCallback(async () => {
    if (!getRefreshToken()) {
      debugError('❌ No refresh token available');
      return false;
    }
    debugLog('🔄 Refreshing access token...');
    const newAccessToken = await refreshAndPersistTokens();
    if (newAccessToken) {
      debugLog('✅ Token refresh successful');
      return true;
    }
    debugError('❌ Token refresh failed');
    return false;
  }, []);

  // Detach every handler BEFORE closing so the socket's own onclose can't fire
  // the reconnect path. Used for every intentional close (superseding connect,
  // tab-hidden disconnect, dead-socket teardown, unmount) — without this an
  // intentional close schedules an unwanted reconnect, which was one source of
  // the overnight churn (hidden-disconnect → onclose → reconnect while hidden →
  // repeat every ~70s).
  const hardCloseSocket = useCallback((ws) => {
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try { ws.close(); } catch (_) { /* already closing */ }
  }, []);

  // Single-timer, backed-off reconnect. Guards against the storm: if a connect
  // is already in flight or a reconnect is already scheduled, it does nothing.
  const scheduleReconnect = useCallback(() => {
    if (connectingRef.current) {
      debugLog('⏭️ Reconnect skipped — a connect is already in flight');
      return;
    }
    if (reconnectTimeoutRef.current) {
      debugLog('⏭️ Reconnect skipped — one is already scheduled');
      return;
    }
    const attempt = reconnectAttemptsRef.current;
    const delay = Math.min(RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
    reconnectAttemptsRef.current = attempt + 1;
    debugLog(`⏳ Scheduling reconnect in ${delay}ms (attempt ${attempt + 1})`);
    diag('WS_RECONNECT_SCHEDULED', { attempt: attempt + 1, delayMs: delay });
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (connectRef.current) connectRef.current();
    }, delay);
  }, []);

  // Send JSON message via WebSocket
  // Used for initial authentication message and future streaming commands
  const sendMessage = useCallback((service, func, data, token = null, userId = null) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      debugWarn('WebSocket not ready');
      return;
    }

    // Get JWT token
    const jwtToken = token || getAccessToken();

    if (!jwtToken) {
      debugError('No JWT token available for WebSocket message');
      return;
    }

    // Create message with JWT in header.token field
    const message = {
      header: {
        token: jwtToken,  // JWT token identifies the user session
        service: service,
        function: func,
        correlationID: generateCorrelationId()
      },
      ...data  // Spread data fields at root level
    };

    const messageStr = JSON.stringify(message);
    wsRef.current.send(messageStr);

    debugLog('→ WebSocket sent:', service, func);
    addLog(`Sent ${service}.${func}`);
  }, []);

  // Merge a one-time per-receiver snapshot (fetched over HTTP via getReceiverSnapshot) into
  // the live aircraft store. Used on receiver-switch: the WebSocket only streams incremental
  // aircraft_update messages, so without this an already-validated aircraft shows blue until
  // its next position frame arrives. The snapshot carries the latched validated position (any
  // age), so merging it here renders those aircraft verified immediately.
  //
  // Guarded on the current selection: a snapshot that resolves after the user has switched
  // again is discarded so it can't repopulate the wrong receiver's map. Entries are stamped
  // with the same FE bookkeeping fields (receiverUuid/lastSeen/seenCounter/inactivityTime) the
  // aircraft_update path uses, so the 60s inactivity sweep and receiver filter treat them
  // identically to streamed updates.
  const mergeSnapshot = useCallback((receiverUuid, aircraftArray) => {
    if (!receiverUuid || !Array.isArray(aircraftArray) || aircraftArray.length === 0) {
      return;
    }

    const currentSelectedUuid = selectedReceiverUuidRef.current;
    const isAllReceivers = currentSelectedUuid === 'ALL_RECEIVERS' ||
      currentSelectedUuid.toUpperCase() === 'ALL_RECEIVERS';

    // Only apply if the snapshot is for the receiver still selected (All Receivers accepts any)
    if (!isAllReceivers && receiverUuid !== currentSelectedUuid) {
      debugLog(`🚫 Discarding stale snapshot for ${receiverUuid} (selected: ${currentSelectedUuid})`);
      return;
    }

    const lastSeenTimestamp = Date.now();
    setData(prevData => {
      const merged = (prevData && Object.keys(prevData).length > 0) ? { ...prevData } : {};
      aircraftArray.forEach(aircraft => {
        if (!aircraft || !aircraft.hex) {
          return;
        }
        const existing = merged[aircraft.hex] || {};
        merged[aircraft.hex] = {
          ...existing,
          ...aircraft,
          // Snapshot carries the latched validated position; fall back to existing only if absent
          lat: aircraft.lat != null ? aircraft.lat : existing.lat,
          lon: aircraft.lon != null ? aircraft.lon : existing.lon,
          receiverUuid: receiverUuid,
          lastSeen: lastSeenTimestamp,
          seenCounter: 0,
          inactivityTime: 0
        };
      });
      return merged;
    });

    const withPos = aircraftArray.filter(a => a.lat != null && a.lon != null).length;
    debugLog(`📸 Snapshot merged for ${receiverUuid}: ${aircraftArray.length} aircraft (${withPos} with position)`);
  }, []);

  // Connect for authenticated users (on mount or when auth status changes)
  useEffect(() => {
    if (isAuthenticated) {
      debugLog('🔌 useWebSocket hook mounting - User is authenticated, connecting WebSocket for data stream...');

      // connect() itself supersedes any existing socket and clears any pending
      // reconnect timer, so just reset backoff and connect.
      reconnectAttemptsRef.current = 0;
      connect();

      // Cleanup on unmount
      return () => {
        debugLog('🔌 useWebSocket hook unmounting - Cleaning up WebSocket connection');
        // Detach-then-close so the unmounting socket can't schedule a reconnect.
        hardCloseSocket(wsRef.current);
        wsRef.current = null;
        connectingRef.current = false;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
      };
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab visibility changes - disconnect when tab sleeps too long, reconnect when it wakes
  // This prevents message queuing on the server when the client can't process them
  const visibilityTimeoutRef = useRef(null);
  const TAB_HIDDEN_DISCONNECT_DELAY = 60000; // 60 seconds before disconnecting hidden tab

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is now hidden - start timer to disconnect after delay
        // This allows for quick tab switches without disconnecting
        debugLog(`😴 Tab hidden - will disconnect WebSocket in ${TAB_HIDDEN_DISCONNECT_DELAY / 1000}s if still hidden`);

        // Clear any existing visibility timeout
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
        }

        // Set timeout to disconnect if tab stays hidden
        visibilityTimeoutRef.current = setTimeout(() => {
          if (document.hidden) {
            debugLog('😴 Tab still hidden after timeout - closing WebSocket to prevent message queuing');
            addLog('Tab hidden too long - disconnecting');
            diag('WS_TAB_HIDDEN_CLOSE');

            // Clear any pending reconnection
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = null;
            }

            // Detach-then-close so this intentional close does NOT fire the
            // onclose reconnect path (which would re-open a socket we're
            // deliberately dropping because the tab is hidden — an overnight
            // churn source).
            hardCloseSocket(wsRef.current);
            wsRef.current = null;
            connectingRef.current = false;

            // Clear ping interval
            if (pingIntervalRef.current) {
              clearInterval(pingIntervalRef.current);
              pingIntervalRef.current = null;
            }

            setStatus('disconnected');
          }
        }, TAB_HIDDEN_DISCONNECT_DELAY);
      } else {
        // Tab is now visible - cancel any pending disconnect and reconnect if needed
        debugLog('👁️ Tab visible');

        // Cancel pending disconnect timeout
        if (visibilityTimeoutRef.current) {
          clearTimeout(visibilityTimeoutRef.current);
          visibilityTimeoutRef.current = null;
        }

        // Reconnect if the socket is closed OR looks open but is stale (no
        // inbound traffic within the liveness window — the exact state a
        // laptop resuming from sleep leaves behind, where readyState still
        // wrongly reports OPEN). Don't trust readyState alone here.
        const notOpen = !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN;
        const stale = Date.now() - lastInboundAtRef.current > LIVENESS_TIMEOUT;
        if (notOpen || stale) {
          debugLog(`👁️ Tab visible - reconnecting WebSocket (notOpen=${notOpen}, stale=${stale})`);
          addLog('Tab visible - reconnecting');
          diag('WS_TAB_RESUME_RECONNECT', { notOpen, stale });

          // Clear aircraft data to start fresh (avoid stale data)
          setData({});

          // Reset backoff for a user-initiated resume and reconnect. connect()
          // is guarded and supersedes any half-dead socket, so call it directly.
          reconnectAttemptsRef.current = 0;
          if (connectRef.current) connectRef.current();
        }
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: userEmail and authCode are no longer used for WebSocket connection
  // Login and authorization are handled via HTTP API
  // WebSocket is only used for authenticated users with JWT tokens

  const connect = () => {
    // Guard against overlapping connects — the root cause of the overnight
    // reconnect storm. Multiple triggers (onclose reconnect, visibility regain,
    // liveness watchdog) could previously each spawn a socket that never closed
    // the others, multiplying sockets + timers until the tab ran out of memory.
    if (connectingRef.current) {
      debugLog('⏭️ connect() skipped — already connecting');
      diag('WS_CONNECT_SKIP', { reason: 'already-connecting' });
      return;
    }
    // A pending reconnect is now moot; we're connecting right now.
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    // Tear down any prior socket (handlers detached) before opening a new one.
    if (wsRef.current) {
      debugLog('⚠️ Superseding existing WebSocket before opening a new one');
      hardCloseSocket(wsRef.current);
      wsRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    try {
      connectingRef.current = true;
      lastInboundAtRef.current = Date.now();
      totalConnectsRef.current += 1;
      diag('WS_CONNECT_START', { attempt: reconnectAttemptsRef.current, totalConnects: totalConnectsRef.current });
      setStatus('connecting');
      const wsUrl = config.AVIOTRACK_WS_URL;
      addLog(`Connecting to ${wsUrl}`);

      // Debug: Log current auth state
      debugLog('🔌 WebSocket connecting - Auth state:', {
        isAuthenticated: isAuthenticatedRef.current,
        hasToken: !!getAccessToken(),
        hasEmail: !!userEmailRef.current,
        hasAuthCode: !!authCodeRef.current
      });

      const ws = new WebSocket(wsUrl);

      // Set wsRef immediately so sendMessage can use it in onopen handler
      wsRef.current = ws;

      ws.onopen = () => {
        debugLog('WebSocket connected to /websocket/jwt');
        addLog('Connected successfully');
        setStatus('connected');
        // Healthy open: clear the connecting guard and reset backoff + liveness.
        connectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        lastInboundAtRef.current = Date.now();
        diag('WS_OPEN', { totalConnects: totalConnectsRef.current });

        // Start ping interval to keep connection alive
        // Send ping every 10 seconds to prevent timeout (backend disconnects if no ping for 60s)
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        pingIntervalRef.current = setInterval(() => {
          // Liveness watchdog: if we've heard nothing back (no pong, no data)
          // for LIVENESS_TIMEOUT despite pinging, the socket is dead even if
          // readyState still claims OPEN (Firefox post-sleep). Tear it down and
          // reconnect instead of pinging into the void forever.
          if (Date.now() - lastInboundAtRef.current > LIVENESS_TIMEOUT) {
            debugWarn('💀 No inbound traffic within liveness window — socket is stale, reconnecting');
            addLog('Connection stale (no server response) - reconnecting');
            diag('WS_STALE_WATCHDOG', {
              sinceInboundSec: Math.round((Date.now() - lastInboundAtRef.current) / 1000),
            });
            if (pingIntervalRef.current) {
              clearInterval(pingIntervalRef.current);
              pingIntervalRef.current = null;
            }
            hardCloseSocket(wsRef.current);
            wsRef.current = null;
            setStatus('disconnected');
            scheduleReconnect();
            return;
          }

          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            const token = getAccessToken();
            if (token) {
              // Send ping with proper header structure including JWT token
              const pingMessage = JSON.stringify({
                header: {
                  token: token,
                  service: 'WebsocketEndpoint',
                  function: 'ping',
                  correlationID: generateCorrelationId()
                },
                messageType: 'ping',
                timestamp: Date.now()
              });
              wsRef.current.send(pingMessage);
              debugLog('🏓 Sent ping to keep connection alive');
            } else {
              debugWarn('🏓 Cannot send ping - no JWT token available');
            }
          }
        }, PING_INTERVAL);

        // Per guide: First message with JWT in header will authenticate the connection
        const token = getAccessToken();

        if (token && isAuthenticatedRef.current) {
          debugLog('🔐 WebSocket connected, sending first message to authenticate user session');

          // Send getAccountInfo as first authenticating message
          // This message includes JWT in header.token to identify the user session
          const savedUserInfo = getUserInfo();
          if (savedUserInfo && savedUserInfo.email) {
            debugLog('📡 Sending getAccountInfo as first authenticating message');
            sendMessage('UserAccountService', 'getAccountInfo', {
              email: savedUserInfo.email
            }, token);

            // Send selectReceiver to restore receiver selection after reconnect
            const currentReceiverUuid = selectedReceiverUuidRef.current;
            if (currentReceiverUuid) {
              debugLog(`📡 Sending selectReceiver after reconnect: ${currentReceiverUuid}`);
              sendMessage('WebsocketEndpoint', 'selectReceiver', {
                receiverUuid: currentReceiverUuid
              }, token);
            }
            setConnectionEpoch(epoch => epoch + 1);
          } else {
            debugError('❌ Cannot send authenticating message - no email in saved user info');
          }
        } else {
          debugLog('⚠️ WebSocket connected but not authenticated (waiting for HTTP login)');
        }
      };

      ws.onmessage = (event) => {
        // Any inbound frame proves the socket is alive — feeds the watchdog.
        lastInboundAtRef.current = Date.now();
        totalMessagesRef.current += 1;
        try {
          const message = JSON.parse(event.data);

          // MESSAGE PROCESSING ORDER:
          // 1. Connection ready message
          // 2. Data stream messages (messageType: aircraft_update, furthest_distance_update)
          // 3. Service/API responses (header.service and header.function)
          //
          // Note: Data stream messages have BOTH messageType AND header.service/function
          // so we must check messageType FIRST to avoid them being caught as service responses

          // Handle connection ready message
          if (message.status === 'ready') {
            debugLog('✅ WebSocket ready:', message.message);
            addLog('WebSocket ready for authentication');
            return;
          }

          // Handle pong response to ping
          if (message.messageType === 'pong') {
            debugLog('🏓 Received pong response from server');
            return;
          }

          // Handle data stream messages FIRST (before service responses)

          // Handle furthest distance update messages
          if (message.messageType === 'furthest_distance_update') {
            const isAllTime = message.allTime === true;
            debugLog(`📍 Received furthest_distance_update (${isAllTime ? 'ALL-TIME' : 'daily'}):`, {
              messageType: message.messageType,
              receiverUuid: message.receiverUuid,
              recordCount: message.bearingRecords ? message.bearingRecords.length : 0,
              allTime: isAllTime
            });

            // Merge data by receiverUuid instead of replacing
            // Use separate state for all-time vs daily data
            if (isAllTime) {
              setAllTimeFurthestDistanceData(prevData => {
                const newData = { ...(prevData || {}) };

                // Store this receiver's all-time data
                newData[message.receiverUuid] = {
                  bearingRecords: message.bearingRecords,
                  timestamp: message.timestamp,
                  receiverUuid: message.receiverUuid
                };

                debugLog('📍 Updated ALL-TIME furthestDistanceData - receivers:', Object.keys(newData));
                return newData;
              });
            } else {
              setFurthestDistanceData(prevData => {
                const newData = { ...(prevData || {}) };

                // Store this receiver's daily data
                newData[message.receiverUuid] = {
                  bearingRecords: message.bearingRecords,
                  timestamp: message.timestamp,
                  receiverUuid: message.receiverUuid
                };

                debugLog('📍 Updated daily furthestDistanceData - receivers:', Object.keys(newData));
                return newData;
              });
            }
            return;
          }

          // Handle aircraft update messages
          if (message.messageType === 'aircraft_update' && message.aircraft && Array.isArray(message.aircraft)) {
            // Filter by selected receiver UUID
            const currentSelectedUuid = selectedReceiverUuidRef.current;
            const messageReceiverUuid = message.receiverUuid;

            // Log received aircraft count with receiver info
            const totalReceived = message.aircraft.length;
            const withPosition = message.aircraft.filter(ac => ac.lat != null && ac.lon != null).length;
            const withoutPosition = totalReceived - withPosition;
            debugLog(`✈️ Aircraft update from receiver ${messageReceiverUuid}: ${totalReceived} total (${withPosition} with position, ${withoutPosition} without position) [selected: ${currentSelectedUuid}]`);

            // Check if this message should be processed
            const shouldProcess =
              currentSelectedUuid === 'ALL_RECEIVERS' ||
              currentSelectedUuid.toUpperCase() === 'ALL_RECEIVERS' ||
              messageReceiverUuid === currentSelectedUuid;

            if (!shouldProcess) {
              // Skip this message - it's from a different receiver
              debugLog(`🚫 Filtering out aircraft_update from receiver ${messageReceiverUuid} (selected: ${currentSelectedUuid})`);
              return;
            }

            // Filter out stale messages (older than 30 seconds)
            // This handles cases where messages were queued during tab sleep
            // Note: server sends LocalDateTime (UTC, no timezone suffix) so we append 'Z'
            // to ensure JavaScript parses it as UTC, not local time (DST-safe)
            if (message.timestamp) {
              const tsStr = String(message.timestamp);
              const messageTime = new Date(tsStr.endsWith('Z') ? tsStr : tsStr + 'Z').getTime();
              const now = Date.now();
              const ageSeconds = (now - messageTime) / 1000;

              if (ageSeconds > 30) {
                debugLog(`🚫 Filtering out stale aircraft_update (${ageSeconds.toFixed(1)}s old)`);
                return;
              }
            }

            // Increment update counter for updates/second calculation
            updateCounterRef.current += message.aircraft.length;

            // Accumulate aircraft updates into pending data (throttled to reduce re-renders)
            // This improves touch responsiveness on mobile devices
            // ALL aircraft are included (with and without positions)
            const lastSeenTimestamp = Date.now();

            message.aircraft.forEach(aircraft => {
              if (!pendingDataRef.current) {
                pendingDataRef.current = {};
              }

              // Debug: Log raw data for new aircraft (last_seen_at is null/undefined)
              // if (!aircraft.last_seen_at) {
              //   console.log('🆕 NEW AIRCRAFT RAW DATA:', JSON.stringify(aircraft, null, 2));
              // }

              // Merge with existing data to preserve position if new message doesn't include it
              const existingAircraft = pendingDataRef.current[aircraft.hex] || {};
              pendingDataRef.current[aircraft.hex] = {
                ...existingAircraft,
                ...aircraft,
                // Only update lat/lon if new values are provided (don't overwrite with undefined)
                lat: aircraft.lat != null ? aircraft.lat : existingAircraft.lat,
                lon: aircraft.lon != null ? aircraft.lon : existingAircraft.lon,
                receiverUuid: messageReceiverUuid, // Track which receiver this aircraft came from
                lastSeen: lastSeenTimestamp,
                seenCounter: 0,
                inactivityTime: 0 // Reset inactivity timer when new position received
              };
            });

            // Throttled flush of accumulated data to React state
            const now = Date.now();
            const timeSinceLastFlush = now - lastDataFlushRef.current;

            if (timeSinceLastFlush >= DATA_UPDATE_THROTTLE) {
              // Enough time has passed, flush immediately
              if (pendingDataRef.current && Object.keys(pendingDataRef.current).length > 0) {
                const pendingUpdates = pendingDataRef.current;
                pendingDataRef.current = null;
                lastDataFlushRef.current = now;

                // Filter pending updates to only include aircraft from currently selected receiver
                const currentSelectedUuid = selectedReceiverUuidRef.current;
                const isAllReceivers = currentSelectedUuid === 'ALL_RECEIVERS' || currentSelectedUuid.toUpperCase() === 'ALL_RECEIVERS';

                setData(prevData => {
                  const normalizedPrevData = (prevData && Object.keys(prevData).length > 0) ? prevData : {};

                  // Merge updates with existing data, preserving positions if not provided in update
                  const mergeWithExisting = (updates) => {
                    const merged = { ...normalizedPrevData };
                    Object.entries(updates).forEach(([hex, aircraft]) => {
                      const existing = merged[hex] || {};
                      merged[hex] = {
                        ...existing,
                        ...aircraft,
                        // Preserve lat/lon from existing if not provided in update
                        lat: aircraft.lat != null ? aircraft.lat : existing.lat,
                        lon: aircraft.lon != null ? aircraft.lon : existing.lon,
                      };
                    });
                    return merged;
                  };

                  if (isAllReceivers) {
                    return mergeWithExisting(pendingUpdates);
                  }

                  // Filter to only add aircraft from currently selected receiver
                  const filteredUpdates = {};
                  let filteredCount = 0;
                  Object.entries(pendingUpdates).forEach(([hex, aircraft]) => {
                    if (aircraft.receiverUuid === currentSelectedUuid) {
                      filteredUpdates[hex] = aircraft;
                    } else {
                      filteredCount++;
                    }
                  });

                  if (filteredCount > 0) {
                    debugLog(`🛡️ Flush filter: blocked ${filteredCount} aircraft from wrong receiver`);
                  }

                  return mergeWithExisting(filteredUpdates);
                });
              }
            } else if (!dataFlushTimerRef.current) {
              // Schedule a flush for later
              dataFlushTimerRef.current = setTimeout(() => {
                dataFlushTimerRef.current = null;
                if (pendingDataRef.current && Object.keys(pendingDataRef.current).length > 0) {
                  const pendingUpdates = pendingDataRef.current;
                  pendingDataRef.current = null;
                  lastDataFlushRef.current = Date.now();

                  // Filter pending updates to only include aircraft from currently selected receiver
                  const currentSelectedUuid = selectedReceiverUuidRef.current;
                  const isAllReceivers = currentSelectedUuid === 'ALL_RECEIVERS' || currentSelectedUuid.toUpperCase() === 'ALL_RECEIVERS';

                  setData(prevData => {
                    const normalizedPrevData = (prevData && Object.keys(prevData).length > 0) ? prevData : {};

                    // Merge updates with existing data, preserving positions if not provided in update
                    const mergeWithExisting = (updates) => {
                      const merged = { ...normalizedPrevData };
                      Object.entries(updates).forEach(([hex, aircraft]) => {
                        const existing = merged[hex] || {};
                        merged[hex] = {
                          ...existing,
                          ...aircraft,
                          // Preserve lat/lon from existing if not provided in update
                          lat: aircraft.lat != null ? aircraft.lat : existing.lat,
                          lon: aircraft.lon != null ? aircraft.lon : existing.lon,
                        };
                      });
                      return merged;
                    };

                    if (isAllReceivers) {
                      return mergeWithExisting(pendingUpdates);
                    }

                    // Filter to only add aircraft from currently selected receiver
                    const filteredUpdates = {};
                    let filteredCount = 0;
                    Object.entries(pendingUpdates).forEach(([hex, aircraft]) => {
                      if (aircraft.receiverUuid === currentSelectedUuid) {
                        filteredUpdates[hex] = aircraft;
                      } else {
                        filteredCount++;
                      }
                    });

                    if (filteredCount > 0) {
                      debugLog(`🛡️ Delayed flush filter: blocked ${filteredCount} aircraft from wrong receiver`);
                    }

                    return mergeWithExisting(filteredUpdates);
                  });
                }
              }, DATA_UPDATE_THROTTLE - timeSinceLastFlush);
            }
            return;
          }

          // Handle receiver statistics update messages
          if (message.messageType === 'receiver_statistics_update') {
            // Only update if data actually changed to prevent unnecessary re-renders
            const statsString = JSON.stringify(message.receivers);
            if (lastReceiverStatsRef.current !== statsString) {
              lastReceiverStatsRef.current = statsString;
              setReceiverStatistics(message);
            }
            return;
          }

          // Handle receiver connection events (for admin monitoring)
          if (message.messageType === 'receiver_connection_event') {
            debugLog('🔌 Receiver connection event:', message.event?.eventType, message.event?.message);
            setConnectionEvents(prev => {
              // Add new event at the beginning and keep max 100 events
              const newEvents = [message.event, ...prev].slice(0, 100);
              return newEvents;
            });
            return;
          }

          // Handle airport activity update messages (arrivals, departures, landings)
          if (message.messageType === 'airport_activity_update') {
            debugLog('🛬 Airport activity update:', message.eventType, message.aircraft?.icaoAddress, 'at', message.airportIcao);

            // Filter by selected receiver UUID
            const currentSelectedUuid = selectedReceiverUuidRef.current;
            if (currentSelectedUuid !== 'ALL_RECEIVERS' &&
                currentSelectedUuid.toUpperCase() !== 'ALL_RECEIVERS' &&
                message.receiverUuid !== currentSelectedUuid) {
              debugLog(`🚫 Filtering out airport_activity_update from receiver ${message.receiverUuid} (selected: ${currentSelectedUuid})`);
              return;
            }

            // Update state with new airport activity - includes counts for UI updates
            setAirportActivityUpdate({
              ...message,
              receivedAt: Date.now()
            });
            return;
          }

          // Handle NOTAM update messages (global, not per-receiver)
          if (message.messageType === 'notam_update') {
            debugLog('📋 NOTAM update:', message.action, message.notam?.notamId);
            setNotamUpdates(prev => {
              const updated = [{ ...message, receivedAt: Date.now() }, ...prev];
              return updated.length > 200 ? updated.slice(0, 200) : updated;
            });
            return;
          }

          // Handle prediction update messages (global, airport-scoped by consumer)
          if (message.messageType === 'prediction_update' && Array.isArray(message.events)) {
            debugLog('🎯 Prediction update:', message.count, 'event(s)');
            setPredictionUpdate({ ...message, receivedAt: Date.now() });
            return;
          }

          // Departure forecast updates (engines-on -> liftoff ETT)
          if (message.messageType === 'departure_forecast_update' && Array.isArray(message.events)) {
            debugLog('🛫 Departure forecast update:', message.count, 'event(s)');
            setDepartureForecastUpdate({ ...message, receivedAt: Date.now() });
            return;
          }

          // Handle ACARS message updates
          if (message.messageType === 'acars_message') {
            debugLog('📡 ACARS message:', message.message?.address, message.message?.label,
              message.message?.registration ? `reg=${message.message.registration}` : '',
              message.message?.aircraftType ? `type=${message.message.aircraftType}` : '',
              message.message?.ownerOperator ? `op=${message.message.ownerOperator}` : '');

            // Filter by selected receiver UUID
            const currentSelectedUuid = selectedReceiverUuidRef.current;
            if (currentSelectedUuid !== 'ALL_RECEIVERS' &&
                currentSelectedUuid.toUpperCase() !== 'ALL_RECEIVERS' &&
                message.receiverUuid !== currentSelectedUuid) {
              return;
            }

            // Append to ACARS messages (FIFO, cap at 200 for live view)
            setAcarsMessages(prev => {
              const updated = [{ ...message.message, receiverUuid: message.receiverUuid, receivedAt: Date.now() }, ...prev];
              return updated.length > 200 ? updated.slice(0, 200) : updated;
            });
            return;
          }

          // Handle service/API responses (primarily for initial authentication via getAccountInfo)
          if (message.header && message.header.service && message.header.function) {
            const service = message.header.service;
            const func = message.header.function;
            const apiStatus = message.header.apiStatus;

            debugLog('← WebSocket received:', service, func, apiStatus);

            // Convert to handler-friendly format
            const handlerMessage = {
              service,
              function: func,
              apiStatus,
              header: message.header,
              ...message
            };

            if (onAuthResponseRef.current) {
              onAuthResponseRef.current(handlerMessage);
            }

            return;
          }
        } catch (error) {
          debugError('Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        debugError('WebSocket error:', error);
        addLog(`Error: ${error.message || 'Connection failed'}`);
        setStatus('error');
        // onclose fires after onerror and owns the reconnect decision.
      };

      ws.onclose = async (event) => {
        debugLog('WebSocket closed', { code: event.code, reason: event.reason });
        addLog(`Connection closed (code: ${event.code}, reason: ${event.reason || 'No reason provided'})`);
        setStatus('disconnected');
        // This handler only runs for a socket we did NOT intentionally close
        // (intentional closes detach handlers via hardCloseSocket), so it is
        // safe to clear the current-socket guard state here.
        connectingRef.current = false;
        wsRef.current = null;
        totalClosesRef.current += 1;
        diag('WS_CLOSE', { code: event.code, reason: event.reason || '', totalCloses: totalClosesRef.current });

        // Clear ping interval on close
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Check if close was due to invalid JWT (look for JWT-related messages in reason)
        const reason = (event.reason || '').toLowerCase();
        const isAuthError = reason.includes('jwt') || reason.includes('token') || reason.includes('auth') || reason.includes('invalid');

        if (isAuthError) {
          debugLog('❌ WebSocket closed due to authentication failure - attempting token refresh');
          addLog('Authentication failed - attempting to refresh token');

          // Attempt to refresh token
          const refreshSuccess = await performTokenRefresh();

          if (refreshSuccess) {
            debugLog('✅ Token refreshed successfully - reconnecting WebSocket');
            addLog('Token refreshed - reconnecting');
            // Fresh token — reset backoff and reconnect immediately (guarded).
            reconnectAttemptsRef.current = 0;
            if (connectRef.current) connectRef.current();
          } else {
            debugError('❌ Token refresh failed - logging out');
            addLog('Token refresh failed - please login again');
            // Call the auth failed callback to clear session and show login
            if (onAuthFailedRef.current) {
              onAuthFailedRef.current();
            }
          }
        } else {
          // Normal disconnect - schedule a single, backed-off reconnection.
          scheduleReconnect();
        }
      };

      // wsRef.current already set right after creating WebSocket (line 126)
    } catch (error) {
      debugError('Error creating WebSocket:', error);
      addLog(`Error: ${error.message}`);
      setStatus('error');
      connectingRef.current = false;

      // Schedule a single, backed-off reconnection.
      scheduleReconnect();
    }
  };

  // Keep connectRef pointing at the latest connect() closure so the reconnect
  // timer and liveness watchdog always invoke the current one (connect is a
  // per-render closure over the state setters / refs above).
  connectRef.current = connect;

  useEffect(() => {
    // Start the 1-second timer to update seen counters and updates per second
    seenTimerRef.current = setInterval(() => {
      // Update updates per second - only if value changed
      const currentCount = updateCounterRef.current;
      if (currentCount > 0) {
        setUpdatesPerSecond(currentCount);
      }
      updateCounterRef.current = 0; // Reset counter

      // Get current receiver selection for filtering
      const currentSelectedUuid = selectedReceiverUuidRef.current;
      const isAllReceivers = currentSelectedUuid === 'ALL_RECEIVERS' ||
                             (currentSelectedUuid && currentSelectedUuid.toUpperCase() === 'ALL_RECEIVERS');

      setData(prevData => {
        if (!prevData || typeof prevData !== 'object' || Object.keys(prevData).length === 0) {
          return prevData;
        }

        const now = Date.now();
        const updatedData = {};
        let hasChanges = false;
        let filteredByReceiverCount = 0;

        Object.keys(prevData).forEach(hex => {
          const aircraft = prevData[hex];

          // DEFENSIVE: Filter out aircraft from wrong receiver
          // This catches any aircraft that slipped through due to race conditions
          if (!isAllReceivers && aircraft.receiverUuid && aircraft.receiverUuid !== currentSelectedUuid) {
            filteredByReceiverCount++;
            hasChanges = true;
            return; // Don't add to updatedData - removes the aircraft
          }

          if (!aircraft.lastSeen) {
            // Keep aircraft without lastSeen unchanged
            updatedData[hex] = aircraft;
            return;
          }

          const secondsSinceLastSeen = Math.floor((now - aircraft.lastSeen) / 1000);

          // Remove aircraft if not seen for 60 seconds
          if (secondsSinceLastSeen >= 60) {
            // Don't add to updatedData - this removes the aircraft
            hasChanges = true;
            return;
          }

          // Update inactivity time if it changed
          if (aircraft.inactivityTime !== secondsSinceLastSeen) {
            updatedData[hex] = {
              ...aircraft,
              inactivityTime: secondsSinceLastSeen
            };
            hasChanges = true;
          } else {
            updatedData[hex] = aircraft;
          }
        });

        // Log if we filtered out aircraft from wrong receiver
        if (filteredByReceiverCount > 0) {
          debugLog(`🛡️ Timer cleanup: removed ${filteredByReceiverCount} aircraft from wrong receiver`);
        }

        // Only return new object if there were changes
        if (hasChanges) {
          // Check if we removed all aircraft - if so, return null instead of empty object
          if (Object.keys(updatedData).length === 0) {
            return null;
          }
          return updatedData;
        }

        return prevData;
      });
    }, 1000);

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (seenTimerRef.current) {
        clearInterval(seenTimerRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (dataFlushTimerRef.current) {
        clearTimeout(dataFlushTimerRef.current);
      }
      // Detach-then-close so teardown can't schedule a post-unmount reconnect.
      hardCloseSocket(wsRef.current);
      wsRef.current = null;
      connectingRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, data, furthestDistanceData, allTimeFurthestDistanceData, receiverStatistics, connectionEvents, logs, updatesPerSecond, airportActivityUpdate, acarsMessages, notamUpdates, predictionUpdate, departureForecastUpdate, connectionEpoch, ws: wsRef.current, sendMessage, mergeSnapshot, addLog };
}

export default useWebSocket;